import { Component, Input, OnChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import { ValidationRunResult } from '../validate-button/validation-run-result';

/**
 * Self-contained validation entry point: a "Validate All" button plus the
 * results popup and its state. Drop `<app-validate-entity entityType="X"
 * [entityId]="record?.id" [entityObjectId]="record?._id">` into any view
 * (including bespoke ones that don't use dynamic-view-v2) to enable validation.
 *
 * Per-section buttons can share this popup by feeding results into `onResults`
 * (e.g. via a @ViewChild reference).
 *
 * Deep link support: when the page is opened with `?validate=all` (the link the
 * Slack DM sends), it auto-runs Validate All once — silently (notify=false) so
 * the user isn't DM'd again — and shows the popup.
 */
@Component({
  selector: 'app-validate-entity',
  templateUrl: './validate-entity.component.html',
})
export class ValidateEntityComponent implements OnChanges {
  /** Registered Mongoose model name, e.g. 'Item', 'Challenge'. */
  @Input() entityType!: string;
  @Input() entityId?: number;
  @Input() entityObjectId?: string;
  /** Render the built-in "Validate All" button (false when the host provides its own). */
  @Input() showAllButton = true;
  @Input() styleClass = 'p-button-help';

  validationResult: ValidationRunResult | null = null;
  private autoRan = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private spinnerService: SpinnerService,
  ) {}

  ngOnChanges(): void {
    // Inputs (entityId/_id) arrive once the record loads — try the deep-link then.
    this.maybeAutoValidate();
  }

  /** Show a run result in the popup (called by the built-in button and any host buttons). */
  onResults(result: ValidationRunResult): void {
    this.validationResult = result;
  }

  private maybeAutoValidate(): void {
    if (this.autoRan) return;
    if (this.route.snapshot.queryParamMap.get('validate') !== 'all') return;
    if (this.entityObjectId == null && this.entityId == null) return;
    this.autoRan = true;
    this.autoValidate();
  }

  private async autoValidate(): Promise<void> {
    this.spinnerService.loadSpinner();
    try {
      const result = await this.dataService.validateEntity(
        this.entityType,
        this.entityId as number,
        undefined,
        this.entityObjectId,
        false, // silent: the DM link already notified the user
      );
      this.onResults(result);
    } catch {
      // best-effort
    } finally {
      this.spinnerService.endSpinner();
      this.clearAutoParam();
    }
  }

  /** Remove ?validate=all so it doesn't re-fire on later interactions. */
  private clearAutoParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { validate: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
